import { useQuery, useReactiveVar } from '@apollo/client';
import { ChoiceGroup, IDropdownOption, Stack } from '@fluentui/react';
import {
  DropdownFilter,
  FilterArrayType,
  FilterElement,
} from 'common/components/Filters';
import { getUpdatedFilterArray } from 'common/components/Filters/utils';
import {
  ColumnData,
  SearchBar,
  SearchBarProps,
} from 'common/components/SearchBar';
import { formatDropdownOptions } from 'common/utils';
import { choiceGroupStyles, options } from 'common/utils/listViewOptionStyle';
import React, { useState } from 'react';
import { globalMode } from 'utility/cache/ui';
import { setTAListViewOptions } from '..';
import { TravelerAuthorizationFilterData } from './__generated__/TravelerAuthorizationFilterData';
import { useStyles } from './index.styles';
import TRAVEL_AUTHORIZATION_FILTER_DATA from './TravelerAuthorizationFilterData.graphql';

interface TASearchBarProps extends SearchBarProps {
  onHintsViewToggle?: () => void;
  columns: ColumnData[];
  onFilterChange: (filterArray: FilterArrayType[]) => void;
  onToggleVisibility?: (columns: ColumnData[]) => void;
}

export const TASearchBar: React.FC<TASearchBarProps> = ({
  columns,
  onFilterChange,
  onEnterPress,
  hintsAvailable,
  onRefresh,
  searchInputPlaceholderText,
  onToggleVisibility,
  onHintsViewToggle,
  urlParams,
  onGlobalToggle,
}) => {
  const styles = useStyles();
  const [filterTypes, setFilterTypes] = useState<FilterArrayType[]>([]);
  const TAListViewOptions = useReactiveVar(setTAListViewOptions);
  const globalState = useReactiveVar(globalMode);

  const { data: travelerAuthorizationFiltersData } =
    useQuery<TravelerAuthorizationFilterData>(TRAVEL_AUTHORIZATION_FILTER_DATA);

  const departmentOptions = formatDropdownOptions(
    travelerAuthorizationFiltersData?.companyDepartments?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: true,
    }
  );

  const businessUnitOptions = formatDropdownOptions(
    travelerAuthorizationFiltersData?.companyBusinessUnits?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: true,
    }
  );
  const workgroupOptions = formatDropdownOptions(
    travelerAuthorizationFiltersData?.availableTripWorkgroups?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: true,
    }
  );
  const originOptions: IDropdownOption[] = [
    { key: 1, text: 'Corporate' },
    { key: 0, text: 'Production' },
    { key: -1, text: 'All' },
  ];

  const currencyOptions = formatDropdownOptions(
    travelerAuthorizationFiltersData?.companyCurrencies?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: true,
    }
  );

  const statusOptions = formatDropdownOptions(
    travelerAuthorizationFiltersData?.travelAuthorizationStatusTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.statusType || '',
    }
  );
  const createdByOptions = formatDropdownOptions(
    travelerAuthorizationFiltersData?.travelAuthorizationUsers?.nodes,
    {
      getKey: (item) => item.userId || '',
      getText: (item) => item.userName || '',
      includeAll: true,
    }
  );

  const onOptionChange = (filterdata: FilterArrayType) => {
    const availableFilters = [...filterTypes];
    const newFilters = getUpdatedFilterArray(filterdata, availableFilters);
    onFilterChange?.(newFilters);
    setFilterTypes(newFilters);
  };

  const filters: FilterElement[] = [
    <DropdownFilter
      filterKey={'departmentId'}
      options={departmentOptions || []}
      placeholder={'Department'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey={'businessUnitId'}
      options={businessUnitOptions || []}
      placeholder={'Business Unit'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey={'_isCorporateTravelAuthorization'}
      options={originOptions || []}
      placeholder={'Origins'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey={'corporateWorkgroupId'}
      options={workgroupOptions || []}
      placeholder={'Workgroup'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey={'currencyId'}
      options={currencyOptions || []}
      placeholder={'Currency'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey="filterMultiStatusTypes"
      options={statusOptions}
      placeholder="Status"
      onOptionChange={onOptionChange}
      maxWidth={150}
      multiSelect
    />,
    <DropdownFilter
      filterKey="_createdByUserid"
      options={createdByOptions}
      placeholder="Created By"
      onOptionChange={onOptionChange}
    />,
  ];

  return (
    <SearchBar
      urlParams={urlParams}
      onFilterClear={() => {
        if (filterTypes.length) onFilterChange?.([]);
        setFilterTypes([]);
      }}
      columns={columns}
      searchInputPlaceholderText={searchInputPlaceholderText}
      onEnterPress={onEnterPress}
      onRefresh={onRefresh}
      onGlobalToggle={onGlobalToggle}
      hintsAvailable={hintsAvailable}
      onHintsViewToggle={onHintsViewToggle}
      onToggleVisibility={onToggleVisibility}
      renderActions={
        <Stack
          className={styles.actionsWrapperStyle}
          horizontalAlign="center"
          horizontal
          tokens={{ childrenGap: 25 }}
        >
          <ChoiceGroup
            options={options}
            disabled={globalState}
            selectedKey={
              TAListViewOptions.isHistory
                ? 'History'
                : TAListViewOptions.isSigning
                  ? 'Signing'
                  : 'Accounting'
            }
            styles={choiceGroupStyles}
            onChange={(ev, option) => {
              if (!option) return;

              const isSigning = option.key === 'Signing';
              const isHistory = option.key === 'History';

              setTAListViewOptions({
                ...TAListViewOptions,
                isSigning,
                isHistory,
              });
            }}
          />
        </Stack>
      }
      filters={filters}
    />
  );
};
